import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  BarChart, Bar, XAxis, CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";
import {
  CircleHelp, Download, ChevronDown, ImageIcon, ExternalLink,
  Camera, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import axiosInstance from "@/api/axios";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";

// ── Admin whitelist ───────────────────────────────────────────────────────────
const ADMIN_EMAILS = new Set([
  "tyrellpantophlet@gmail.com",
  "itsplaex@gmail.com",
]);

// ── Lightbox ──────────────────────────────────────────────────────────────────
function MediaLightbox({ media, initialIndex = 0, onClose }) {
  const [current, setCurrent] = useState(initialIndex);
  const item = media[current];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setCurrent((i) => Math.min(i + 1, media.length - 1));
      if (e.key === "ArrowLeft")  setCurrent((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [media.length, onClose]);

  if (!item) return null;
  const isImage = item.media_type === "image" || item.media_type === "gif";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative flex flex-col bg-background rounded-2xl shadow-2xl max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {item.label || new Date(item.captured_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
            {item.bin_identifier && (
              <span className="text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                {item.bin_identifier}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{current + 1} / {media.length}</span>
            <a href={item.file_url} target="_blank" rel="noopener noreferrer"
               className="p-1.5 rounded-md hover:bg-muted transition-colors">
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </a>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="relative flex-1 flex items-center justify-center bg-black/5 min-h-[300px] max-h-[65vh] overflow-hidden">
          {isImage ? (
            <img src={item.file_url} alt={item.label || "capture"}
                 className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="flex flex-col items-center gap-3 text-muted-foreground p-8">
              <ImageIcon className="h-16 w-16 opacity-40" />
              <a href={item.file_url} target="_blank" rel="noopener noreferrer"
                 className="text-sm underline text-primary">Open file</a>
            </div>
          )}
          {current > 0 && (
            <button onClick={() => setCurrent((i) => i - 1)}
                    className="absolute left-3 p-2 rounded-full bg-background/90 shadow-lg hover:bg-background border border-border/40">
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {current < media.length - 1 && (
            <button onClick={() => setCurrent((i) => i + 1)}
                    className="absolute right-3 p-2 rounded-full bg-background/90 shadow-lg hover:bg-background border border-border/40">
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>

        {media.length > 1 && (
          <div className="flex gap-2 px-4 py-3 overflow-x-auto border-t border-border/50 bg-muted/30">
            {media.map((m, i) => {
              const isImg = m.media_type === "image" || m.media_type === "gif";
              return (
                <button key={m.id ?? i} onClick={() => setCurrent(i)}
                        className={cn(
                          "flex-shrink-0 h-14 w-14 rounded-md overflow-hidden border-2 transition-all",
                          i === current ? "border-primary" : "border-transparent opacity-60 hover:opacity-90"
                        )}>
                  {isImg
                    ? <img src={m.file_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-muted flex items-center justify-center">
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      </div>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Hover Panel ───────────────────────────────────────────────────────────────
function HoverPanel({ data, allMedia, anchorRect, chartRect, selectedDay, onOpenLightbox, onMouseEnter, onMouseLeave, canSeeImages }) {
  const left = anchorRect.left - chartRect.left + anchorRect.width / 2;
  const top  = anchorRect.top  - chartRect.top;

  const hourValue     = parseInt(data.fullHour);
  const hourFormatted = hourValue % 12 || 12;
  const amPm          = hourValue < 12 ? "AM" : "PM";
  const count         = data.count;

  const mediaForHour = canSeeImages
    ? allMedia.filter((m) => {
        const d = new Date(m.captured_at);
        const amsterdamDate = new Date(d.toLocaleString("en-US", { timeZone: "Europe/Amsterdam" }));
        return amsterdamDate.getDay() === (selectedDay + 1) % 7 && amsterdamDate.getHours() === hourValue;
      })
    : [];

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "absolute",
        left,
        top,
        transform: "translate(-50%, calc(-100% - 10px))",
        zIndex: 30,
        pointerEvents: "auto",
      }}
      className="rounded-xl border border-border/60 bg-background shadow-xl p-3 text-sm w-64"
    >
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-border/40">
        <p className="font-semibold text-foreground">{hourFormatted} {amPm}</p>
        <span className={cn(
          "text-xs px-1.5 py-0.5 rounded-full font-medium",
          count === 0
            ? "bg-muted text-muted-foreground"
            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
        )}>
          {count} event{count !== 1 ? "s" : ""}
        </span>
      </div>

      {canSeeImages && mediaForHour.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
            <Camera className="h-3 w-3" /> Click to view captures
          </p>
          {mediaForHour.slice(0, 5).map((m, i) => {
            const isImg = m.media_type === "image" || m.media_type === "gif";
            return (
              <button
                key={m.id ?? i}
                onClick={() => onOpenLightbox(mediaForHour, i)}
                className="w-full flex items-center gap-2 group hover:bg-muted/60 rounded-md px-1.5 py-1 transition-colors text-left"
              >
                {isImg ? (
                  <img src={m.file_url} alt=""
                       className="h-9 w-9 rounded object-cover flex-shrink-0 border border-border/40" />
                ) : (
                  <div className="h-9 w-9 rounded bg-muted flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate group-hover:text-primary transition-colors">
                    {m.label || new Date(m.captured_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">{m.bin_identifier}</p>
                </div>
                <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100" />
              </button>
            );
          })}
          {mediaForHour.length > 5 && (
            <p className="text-xs text-muted-foreground pl-1 pt-0.5">
              +{mediaForHour.length - 5} more — click any to open gallery
            </p>
          )}
        </div>
      ) : count > 0 ? (
        <p className="text-xs text-muted-foreground italic">No evidence images available.</p>
      ) : (
        <p className="text-xs text-muted-foreground">No events this hour.</p>
      )}
    </div>
  );
}

// ── Custom Bar ────────────────────────────────────────────────────────────────
function HoverBar({ x, y, width, height, payload, onBarEnter, onBarLeave, isHovered, chartHeight }) {
  return (
    <g
      onMouseEnter={(e) => onBarEnter(payload, e)}
      onMouseLeave={onBarLeave}
      style={{ cursor: payload?.count > 0 ? "pointer" : "default" }}
    >
      {isHovered && (
        <rect x={x - 4} y={0} width={width + 8} height={chartHeight}
              fill="hsl(var(--muted-foreground)/0.08)" rx={2} />
      )}
      {height > 0 && (
        <rect x={x} y={y} width={width} height={height}
              fill={payload?.isPeak ? "hsl(var(--bright-red))" : "hsl(var(--chart-6))"}
              rx={2} ry={2} />
      )}
      <rect x={x - 4} y={0} width={width + 8} height={chartHeight} fill="transparent" />
    </g>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export function ContaminationTime({
  className,
  title,
  timeRange = "week",
  bins = [],
  customDateRange = [{ startDate: null, endDate: null, key: "selection" }],
  isCustomDateRange = false,
  userEmail = "",
  ...props
}) {
  const { t } = useTranslation();

  const isAdmin = ADMIN_EMAILS.has((userEmail ?? "").toLowerCase());

  const [activityData, setActivityData] = useState({});
  const [allMedia, setAllMedia]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedDay, setSelectedDay]   = useState(0);
  const [currentDay, setCurrentDay]     = useState(0);

  const [hoveredBar, setHoveredBar]   = useState(null);
  const [hoveredHour, setHoveredHour] = useState(null);
  const [chartHeight, setChartHeight] = useState(300);
  const barLeaveTimer                  = useRef(null);
  const chartWrapperRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    if (!chartWrapperRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setChartWidth(entry.contentRect.width);
    });
    ro.observe(chartWrapperRef.current);
    return () => ro.disconnect();
  }, []);

  const [lightboxMedia, setLightboxMedia]         = useState(null);
  const [lightboxInitIndex, setLightboxInitIndex] = useState(0);

  const exportContainerRef = useRef(null);

  const daysOfWeek = useMemo(() => [
    t("monday"), t("tuesday"), t("wednesday"),
    t("thursday"), t("friday"), t("saturday"), t("sunday"),
  ], [t]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (bins.length > 0) {
          bins.forEach((b) => params.append("bin", b));
        } else {
          params.append("bin", bins);
        }
        params.append("period", timeRange);

        if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
          const s = customDateRange[0].startDate;
          const e = customDateRange[0].endDate;
          params.append("start_date", s instanceof Date ? s.toISOString() : new Date(s).toISOString());
          params.append("end_date",   e instanceof Date ? e.toISOString() : new Date(e).toISOString());
        }

        const [timeRes, mediaRes] = await Promise.all([
          axiosInstance.get(`contamination_time/?${params.toString()}`),
          axiosInstance.get(`contamination_media/?${params.toString()}`),
        ]);

        if (timeRes.data) {
          setActivityData(timeRes.data.usage_data || {});
          setCurrentDay(timeRes.data.current_day || 0);
          setSelectedDay(timeRes.data.current_day || 0);
        }

        setAllMedia(mediaRes.data?.media || []);

      } catch (err) {
        console.error("Failed to fetch contamination data:", err);
        setActivityData({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, JSON.stringify(bins), JSON.stringify(customDateRange), isCustomDateRange]);

  const chartData = useMemo(() => {
    if (!activityData?.[selectedDay]) return [];
    const dayData  = activityData[selectedDay];
    const maxCount = Math.max(...Object.values(dayData), 1);
    return Object.keys(dayData).map((hour) => {
      const hourNum = parseInt(hour);
      const count   = dayData[hour];
      return {
        hour: `${hourNum % 12 || 12}${hourNum < 12 ? "a" : "p"}`,
        count,
        fullHour: hour,
        isPeak: count === maxCount && count > 0,
      };
    });
  }, [activityData, selectedDay]);

  const busiestHour = useMemo(() => {
    if (!activityData?.[selectedDay]) return null;
    const dayData = activityData[selectedDay];
    let maxHour = null, maxVal = 0;
    Object.entries(dayData).forEach(([h, c]) => {
      if (c > maxVal) { maxVal = c; maxHour = h; }
    });
    if (!maxHour || maxVal === 0) return null;
    const hNum = parseInt(maxHour);
    return `${hNum % 12 || 12} ${hNum < 12 ? "AM" : "PM"}`;
  }, [activityData, selectedDay]);

  const xAxisInterval = useMemo(() => {
    if (chartWidth < 360) return 3;
    if (chartWidth < 500) return 1;
    return 0;
  }, [chartWidth]);

  const handleCancelClose = useCallback(() => {
    if (barLeaveTimer.current) {
      clearTimeout(barLeaveTimer.current);
      barLeaveTimer.current = null;
    }
  }, []);

  const handleScheduleClose = useCallback(() => {
    handleCancelClose();
    barLeaveTimer.current = setTimeout(() => {
      setHoveredHour(null);
      setHoveredBar(null);
    }, 300);
  }, [handleCancelClose]);

  const handleBarEnter = useCallback((payload, e) => {
    handleCancelClose();
    if (!payload || !chartWrapperRef.current) return;
    const barRect   = e.currentTarget.getBoundingClientRect();
    const chartRect = chartWrapperRef.current.getBoundingClientRect();
    setChartHeight(chartRect.height);
    setHoveredHour(payload.fullHour);
    setHoveredBar({ data: payload, anchorRect: barRect, chartRect });
  }, [handleCancelClose]);

  const handleExportCSV = async () => {
    if (!activityData?.[selectedDay]) return;
    try {
      const dayData = activityData[selectedDay];
      const section1 = [
        `"--- SECTION 1: CONTAMINATION SUMMARY (${daysOfWeek[selectedDay]}) ---"`,
        `"Shows the number of contamination events per hour for the selected day."`,
        "",
        ["Hour", "Contamination Events"].join(","),
        ...Object.entries(dayData).map(([h, c]) => {
          const hNum = parseInt(h);
          return [`${hNum % 12 || 12} ${hNum < 12 ? "AM" : "PM"}`, c].join(",");
        }),
      ].join("\n");

      let section2 = "";
      try {
        const params = { bins: bins.join(",") };
        if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
          const s = customDateRange[0].startDate;
          const e = customDateRange[0].endDate;
          params.startDate = (s instanceof Date ? s : new Date(s)).toISOString().slice(0, 10);
          params.endDate   = (e instanceof Date ? e : new Date(e)).toISOString().slice(0, 10);
        } else {
          params.timeRange = timeRange;
        }
        const response = await axiosInstance.get("/stats_v2/raw-data", {
          params,
          paramsSerializer: { indexes: null },
        });
        if (response.data && Array.isArray(response.data)) {
          const contaminated = response.data.filter((item) => item.contamination === "Y");
          const detailHeaders = ["Bin","Date & Time","Item","Thrown Into","Expected Stream","Contamination Y/N","Weight"].join(",");
          const detailRows = contaminated.map((item) => {
            let displayTime = "-";
            if (item.time_collected && item.time_collected !== "-") {
              try {
                displayTime = new Date(item.time_collected).toLocaleString("nl-NL", {
                  timeZone: "Europe/Amsterdam", year: "numeric", month: "2-digit",
                  day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
                });
              } catch { displayTime = item.time_collected; }
            }
            return [item.bin||"-", `"${displayTime}"`, item.sub_class_waste_type||"-",
                    item.waste_type||"-", item.expected_stream||"-", item.contamination||"-", item.waste_weight||0].join(",");
          });
          section2 = ["","",`"--------------------------------------------------"`,
            `"--- SECTION 2: CONTAMINATION EVENTS (individual records with timestamps) ---"`,
            `"Each row is one contamination event detected across the selected period."`,
            "", detailHeaders, ...detailRows].join("\n");
        }
      } catch {
        section2 = `\n\n"Could not retrieve individual contamination records at this time."`;
      }

      const csv  = section1 + section2;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `contamination_time_${daysOfWeek[selectedDay]}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleExportPNG = async () => {
    if (!exportContainerRef.current) return;
    const container = exportContainerRef.current;
    container.style.visibility = "visible";
    container.style.pointerEvents = "none";
    window.dispatchEvent(new Event("resize"));
    await new Promise((r) => setTimeout(r, 600));
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(container, {
        scale: 2, useCORS: true, logging: false,
        backgroundColor: "#ffffff", width: 1200, height: 750,
        windowWidth: 1200, windowHeight: 750,
      });
      const link    = document.createElement("a");
      link.download = `contamination_time_${daysOfWeek[selectedDay]}_${new Date().toISOString().slice(0, 10)}.png`;
      link.href     = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("PNG export failed:", err);
    } finally {
      container.style.visibility  = "hidden";
      container.style.pointerEvents = "none";
    }
  };

  if (loading) {
    return (
      <div className={cn("lg:col-span-2 aspect-[16/10] sm:rounded-lg bg-muted/50 p-4", className)} {...props}>
        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{title || "Contamination Time"}</h4>
        <div className="flex items-center justify-center h-[320px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <>
      {lightboxMedia && (
        <MediaLightbox
          media={lightboxMedia}
          initialIndex={lightboxInitIndex}
          onClose={() => setLightboxMedia(null)}
        />
      )}

      <div
        className={cn("exportable-section lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4", className)}
        {...props}
      >
        <div className="flex justify-between items-center mb-2">
          <div>
            <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{title || "Contamination Time"}</h4>
            {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
              <p className="text-sm text-muted-foreground mt-1">
                {(() => {
                  const s = customDateRange[0].startDate instanceof Date
                    ? customDateRange[0].startDate : new Date(customDateRange[0].startDate);
                  const e = customDateRange[0].endDate instanceof Date
                    ? customDateRange[0].endDate : new Date(customDateRange[0].endDate);
                  return !isNaN(s) && !isNaN(e) ? `${s.toLocaleDateString()} – ${e.toLocaleDateString()}` : "";
                })()}
              </p>
            )}
          </div>

          <div className="flex items-center space-x-2 no-export">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm"
                        className="text-muted-foreground hover:text-primary"
                        disabled={!activityData?.[selectedDay]}>
                  <Download className="h-4 w-4 mr-1" /> Export <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportCSV}>Download as CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPNG}>Download as PNG</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <TooltipProvider>
              <UITooltip>
                <TooltipTrigger asChild>
                  <CircleHelp className="h-5 w-5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Hover a bar to see media captures. Click any capture to open the gallery.</p>
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          </div>
        </div>

        <div className="px-2 pt-4 sm:px-6 sm:pt-6 sm:pr-8" style={{ width: "100%", height: "100%" }}>
          {!activityData?.[selectedDay] ? (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground text-center">
              No contamination data available for the selected period
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <div className="mb-3">
                <Tabs
                  value={selectedDay.toString()}
                  onValueChange={(v) => setSelectedDay(parseInt(v))}
                  className="w-full mb-3"
                >
                  <TabsList className="w-full grid grid-cols-7 gap-1 p-1 bg-muted rounded-md">
                    {daysOfWeek.map((day, index) => (
                      <TabsTrigger
                        key={index}
                        value={index.toString()}
                        className={cn(
                          "py-1 text-xs sm:text-sm font-medium relative transition-all",
                          "data-[state=active]:bg-background data-[state=active]:text-foreground",
                          "data-[state=active]:shadow-sm rounded-sm text-muted-foreground hover:text-foreground flex justify-center"
                        )}
                      >
                        <span className="inline-flex items-center">
                          {day.substring(0, 3)}
                          {index === currentDay && (
                            <span className={cn(
                              "ml-1 h-1.5 w-1.5 rounded-full",
                              selectedDay === index ? "bg-primary" : "bg-muted-foreground"
                            )} />
                          )}
                        </span>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>

                {busiestHour && (
                  <div className="flex items-center mt-2">
                    <span className="inline-flex mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span className="font-medium text-red-500 text-sm">{busiestHour}:</span>
                    <span className="ml-2 text-sm text-muted-foreground">Usually most contaminated</span>
                  </div>
                )}
              </div>

              <div className="flex-grow w-full relative" ref={chartWrapperRef}>
                {hoveredBar && (
                  <HoverPanel
                    data={hoveredBar.data}
                    allMedia={allMedia}
                    anchorRect={hoveredBar.anchorRect}
                    chartRect={hoveredBar.chartRect}
                    selectedDay={selectedDay}
                    onOpenLightbox={(media, idx) => {
                      setLightboxMedia(media);
                      setLightboxInitIndex(idx);
                    }}
                    onMouseEnter={handleCancelClose}
                    onMouseLeave={handleScheduleClose}
                    canSeeImages={true}
                  />
                )}

                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 20, left: 5, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false}
                                   stroke="hsl(var(--muted-foreground)/0.2)" />
                    <XAxis
                      dataKey="hour"
                      tick={{ fontSize: 13, fill: "hsl(var(--muted-foreground))" }}
                      axisLine={false} tickLine={false}
                      interval={window.innerWidth < 640 ? 1 : 0}
                    />
                    <Bar
                      dataKey="count"
                      radius={[2, 2, 0, 0]}
                      maxBarSize={16}
                      shape={(shapeProps) => (
                        <HoverBar
                          {...shapeProps}
                          onBarEnter={handleBarEnter}
                          onBarLeave={handleScheduleClose}
                          isHovered={shapeProps.payload?.fullHour === hoveredHour}
                          chartHeight={chartHeight}
                        />
                      )}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Hidden export container */}
        <div
          ref={exportContainerRef}
          style={{
            position: "fixed", top: 0, left: 0,
            width: "1200px", height: "750px",
            visibility: "hidden", pointerEvents: "none",
            zIndex: -1, backgroundColor: "#ffffff",
            borderRadius: "8px", padding: "16px",
            boxSizing: "border-box", display: "flex", flexDirection: "column",
          }}
        >
          <h4 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "8px" }}>
            {title || "Contamination Time"}
          </h4>
          <div style={{ marginBottom: "12px" }}>
            <div style={{ display: "flex", width: "100%", background: "#f1f5f9", borderRadius: "6px", padding: "4px", gap: "4px" }}>
              {daysOfWeek.map((day, index) => (
                <div key={index} style={{
                  flex: 1, textAlign: "center", padding: "6px 0", fontSize: "14px",
                  fontWeight: index === selectedDay ? 600 : 400,
                  color: index === selectedDay ? "#0f172a" : "#64748b",
                  background: index === selectedDay ? "#ffffff" : "transparent",
                  borderRadius: "4px",
                  boxShadow: index === selectedDay ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}>
                  {day.substring(0, 3)}
                  {index === currentDay && (
                    <span style={{
                      display: "inline-block", width: "6px", height: "6px", borderRadius: "50%",
                      background: index === selectedDay ? "#6366f1" : "#94a3b8",
                      marginLeft: "4px", verticalAlign: "middle",
                    }} />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 5, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(100,116,139,0.2)" />
                <XAxis dataKey="hour" tick={{ fontSize: 13, fill: "#64748b" }} axisLine={false} tickLine={false} interval={0} />
                <Bar dataKey="count" radius={[2, 2, 0, 0]} maxBarSize={16}>
                  {chartData.map((entry, index) => (
                    <Cell key={`export-cell-${index}`} fill={entry.isPeak ? "#ef4444" : "#94a3b8"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
}